import { useEffect, useState, type CSSProperties } from 'react';
import QRCodeLib from 'qrcode';
import { SampleFlag } from './Basics';

/** QR code generated in the browser from the real join address (room included). */
export function QRCode({ value, size, label }: { value: string; size: number; label: string }) {
  const [svg, setSvg] = useState('');
  useEffect(() => {
    let live = true;
    QRCodeLib.toString(value, { type: 'svg', margin: 1, errorCorrectionLevel: 'M', color: { dark: '#0E1430', light: '#FBF7EE' } })
      .then((s) => { if (live) setSvg(s); }).catch(() => { if (live) setSvg(''); });
    return () => { live = false; };
  }, [value]);
  return <span className="ex-qr" role="img" aria-label={label} data-qr-value={value} style={{ width: size, height: size }} dangerouslySetInnerHTML={{ __html: svg }} />;
}

/** Join panel: QR + short address + room code. `placeholder` marks sample addresses visibly. */
export function QRJoinPanel({ joinUrl, shortUrl, roomCode, placeholder, size = 'tv' }:
  { joinUrl: string; shortUrl: string; roomCode: string; placeholder?: boolean; size?: 'tv' | 'host' | 'compact' }) {
  const tv = size === 'tv', host = size === 'host';
  const qr = tv ? 300 : host ? 120 : 170;
  const pad: CSSProperties = tv ? { padding: '44px 40px 36px', gridTemplateColumns: '300px minmax(0,1fr)', gap: 36 } : host ? { padding: 20, gridTemplateColumns: '120px minmax(0,1fr)', gap: 16, marginTop: 0 } : { padding: '28px 24px 22px', gridTemplateColumns: '170px minmax(0,1fr)', gap: 22 };
  return (
    <section className="ex-dossier" aria-label="How to join" style={{ display: 'grid', alignItems: 'center', marginTop: host ? 0 : 10, ...pad }}>
      {!host && <span className="ex-dossier__tab" style={tv ? { fontSize: 16 } : undefined}>Apply here</span>}
      <QRCode value={joinUrl} size={qr} label={`QR code that opens ${joinUrl}`} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: tv ? 16 : 6, minWidth: 0 }}>
        <span className="ex-mono" style={{ fontSize: tv ? 20 : 12, color: '#4A4F66' }}>Scan, or go to</span>
        <span style={{ font: `700 ${tv ? 38 : host ? 14 : 22}px/1.1 var(--font-mono)`, overflowWrap: 'anywhere' }}>{shortUrl}</span>
        {placeholder && <SampleFlag style={{ color: '#7A4600', alignSelf: 'flex-start', fontSize: tv ? 15 : 11 }}>Sample address · replace before the party</SampleFlag>}
        <span className="ex-mono" style={{ fontSize: tv ? 20 : 12, color: '#4A4F66', marginTop: tv ? 8 : 2 }}>Room code</span>
        <span className="ex-tnum" style={{ font: `800 ${tv ? 92 : host ? 36 : 48}px/1 var(--font-mono)`, letterSpacing: '.08em', color: '#003399' }}>{roomCode}</span>
      </div>
    </section>
  );
}

/** Lobby chips. Sizes adapt to the entry count so 2, 20 or 50 entries all read well on the TV. */
export function PlayerChips({ players }: { players: { id: string; name: string; isYou: boolean }[] }) {
  const n = players.length;
  const [cols, h, font, pad, gap] = n <= 8 ? [2, 84, 44, 30, 18] : n <= 24 ? [3, 66, 32, 24, 14] : [4, 54, 26, 18, 10];
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap, alignContent: 'start' }} aria-label="Applicants">
      {players.map((p) => (
        <li key={p.id} className="ex-player-chip mo-arrive" data-you={p.isYou} style={{ height: h, padding: `0 ${pad}px`, fontSize: font }} title={p.name}><span>{p.name}</span></li>
      ))}
    </ul>
  );
}
